import React, { useId, useState } from "react";
import { EmptyState } from "../../../../ForBrands/ui/BrandUI";
import CollapsibleCard from "../../../../ForBrands/ui/CollapsibleCard";
import { LegendRow } from "../../../../ForBrands/ui/LegendItem";
import { Icon } from "../../../../ForBrands/ui/icons";
import { CHART } from "../../../../ForBrands/ui/charts/chartTokens";
import { usePhoneTheme } from "../../../../ForBrands/useIsPhone";
import { EMPTY } from "../../../../../utils/perfFormat";
import { GAME_LOG_STATS, gameLogView, rgbaOf, shadeColor } from "../teamPerformanceModel";
import { DetailLines, ResultPill } from "./perfPieces";

// Game Log (BUILD_SPEC B7, Jayson's T3): one row per game in date order.
// Desktop: a table that scrolls inside the card, its header and the game
// column sticky; ORtg, DRtg, eFG%, Opp eFG%, 3P% and TOV% tinted against
// the squad's own season average (green better, red worse, up to ±2
// standard deviations); Loc only when some game has a venue; a game with
// no box score keeps its result and dashes. Phones: a compact list (game,
// result, score, the Net under its own small label) where each game opens
// its full line.

const cx = (...parts) => parts.filter(Boolean).join(" ");
// One text colour per cell: signed figures green or red, a missing value tertiary.
const signTone = (v) => (v === null ? "text-bm-tertiary" : v >= 0 ? "text-bm-positive" : "text-bm-critical");
const cellTone = (column, value) => (value === null ? "text-bm-tertiary" : column.signed ? signTone(value) : "text-bm-ink");

const SHADE_LEGEND = [
  { key: "better", swatch: "bar", color: rgbaOf(CHART.positive, 0.34), label: "Better than the season average" },
  { key: "worse", swatch: "bar", color: rgbaOf(CHART.critical, 0.34), label: "Worse" },
];

// Alignment, stacking and colour are added per cell (two of a kind would clash).
const TH = "bm-th sticky top-0 !h-10 !px-3";

// A column head written short ("ORtg"): the short form for the eye (and the
// mouse's title), its meaning for a screen reader, which keyboard and touch
// users never get from a title.
const HeadText = ({ short, meaning }) => (
  <>
    <span aria-hidden="true">{short}</span>
    <span className="sr-only">{meaning}</span>
  </>
);
const TD = "bm-td !px-3 !py-[10px] bm-body-m text-center";

const GameLogTable = ({ view }) => (
  <div className="max-h-[560px] overflow-auto rounded-[8px] border border-bm-border">
    <table className="w-full min-w-[900px] border-separate border-spacing-0 tabular-nums">
      <thead>
        <tr>
          <th scope="col" className={cx(TH, "left-0 z-[2] !pl-4 text-left")}>
            Game
          </th>
          {view.showLoc && (
            <th scope="col" className={cx(TH, "z-[1] text-center")} title="Home, away or neutral">
              <HeadText short="Loc" meaning="Home, away or neutral" />
            </th>
          )}
          <th scope="col" className={cx(TH, "z-[1] text-center")} title="Result">
            <HeadText short="Res" meaning="Result" />
          </th>
          <th scope="col" className={cx(TH, "z-[1] text-center")}>
            Score
          </th>
          <th scope="col" className={cx(TH, "z-[1] text-center")} title="Margin">
            <HeadText short="Marg" meaning="Margin" />
          </th>
          {GAME_LOG_STATS.map((c) => (
            <th key={c.key} scope="col" className={cx(TH, "z-[1] text-center")} title={c.title}>
              <HeadText short={c.label} meaning={c.title} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {view.rows.map((r) => (
          <tr key={r.key} className="group hover:bg-bm-subtle">
            <th scope="row" className="bm-td sticky left-0 z-[1] bg-white !py-[10px] !pl-4 text-left font-normal group-hover:bg-bm-subtle">
              <span className="flex min-w-[150px] flex-col">
                <span className="bm-label-l text-bm-ink">{r.opponent}</span>
                <span className="bm-body-s text-bm-muted">{r.date}</span>
              </span>
            </th>
            {view.showLoc && <td className={cx(TD, "text-bm-ink")}>{r.loc}</td>}
            <td className={TD}>
              <ResultPill result={r.result} />
            </td>
            <td className={cx(TD, "whitespace-nowrap text-bm-ink")}>{r.score}</td>
            <td className={cx(TD, signTone(r.margin))}>{r.marginText}</td>
            {GAME_LOG_STATS.map((c) => {
              const cell = r.cells[c.key];
              const tint = shadeColor(cell.shade);
              return (
                <td
                  key={c.key}
                  className={cx(TD, cellTone(c, cell.value), c.signed && "font-archivo font-bold")}
                  style={tint ? { background: tint } : undefined}
                >
                  {cell.text}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// One game on a phone: the line opens its box score figures under it.
const GameRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  const detailId = useId();
  const net = row.cells.net;
  return (
    <li className="border-b border-bm-mutedbg last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={detailId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-[10px] py-3 text-left"
      >
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate bm-label-l text-bm-ink">{row.opponent}</span>
          <span className="truncate bm-body-xs text-bm-muted">{[row.date, row.event].filter(Boolean).join(" · ")}</span>
        </span>
        <ResultPill result={row.result} />
        <span className="shrink-0 bm-label-l tabular-nums text-bm-ink">{row.score}</span>
        {/* The figure is named (the list has no column heads), for the eye and the screen reader. */}
        <span className="flex w-[44px] shrink-0 flex-col items-end">
          <span className="bm-overline">Net</span>
          <span className={cx("font-archivo text-[13px] font-bold tabular-nums", signTone(net.value))}>{net.text}</span>
        </span>
        <Icon name="chevron-down" size={16} className={cx("text-bm-muted transition-transform", open ? "rotate-180" : "")} />
      </button>
      <div id={detailId} hidden={!open}>
        <div className="mb-3 rounded-[8px] bg-bm-subtle px-3 py-[10px]">
          {row.hasBox ? (
            <dl className="grid grid-cols-3 gap-x-3 gap-y-2">
              {[["Marg", row.marginText], ...(row.loc !== EMPTY ? [["Loc", row.loc]] : []), ...GAME_LOG_STATS.filter((c) => c.key !== "net").map((c) => [c.label, row.cells[c.key].text])].map(
                ([label, value]) => (
                  <div key={label} className="flex min-w-0 flex-col">
                    <dt className="bm-overline">{label}</dt>
                    <dd className="m-0 font-archivo text-[13px] font-bold tabular-nums text-bm-ink">{value}</dd>
                  </div>
                ),
              )}
            </dl>
          ) : (
            <DetailLines title="No box score for this game" sub={row.margin === null ? null : `Margin ${row.marginText}`} />
          )}
        </div>
      </div>
    </li>
  );
};

const GameLogList = ({ view }) => (
  // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
  <ul role="list" aria-label="Games" className="flex flex-col">
    {view.rows.map((r) => (
      <GameRow key={r.key} row={r} />
    ))}
  </ul>
);

const GameLogCard = ({ payload, collapse }) => {
  const phone = usePhoneTheme();
  const view = gameLogView(payload);
  // The phone list has no shading and no Loc column: its lede says less.
  return (
    <CollapsibleCard title="Game Log" titleAs="h3" subtitle={phone ? view.listNote : view.note} {...collapse}>
      {!view.rows.length && <EmptyState>No games on file for this season yet.</EmptyState>}
      {view.rows.length > 0 && (phone ? <GameLogList view={view} /> : <GameLogTable view={view} />)}
      {view.rows.length > 0 && view.shaded && !phone && <LegendRow items={SHADE_LEGEND} label="Shading" />}
    </CollapsibleCard>
  );
};

export default GameLogCard;
